import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Transaction } from "@appTypes/index";
import getTransactions from "@api/getTransactions";

const INITIAL_DISPLAY_COUNT = 10;

export interface TransactionContextType {
  transactions: Transaction[];
  displayedTransactions: Transaction[];
  areTransactionsLoading: boolean;
  error: string | null;
  isExpanded: boolean;
  toggleExpanded: () => void;
  hasMore: boolean;
}

const defaultContext: TransactionContextType = {
  transactions: [],
  displayedTransactions: [],
  areTransactionsLoading: true,
  error: null,
  isExpanded: false,
  toggleExpanded: () => undefined,
  hasMore: false,
};

export const TransactionContext = createContext<TransactionContextType>(defaultContext);

export const TransactionContextProvider = ({ children }: { children: ReactNode }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [areTransactionsLoading, setAreTransactionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  const displayedTransactions = isExpanded
    ? transactions
    : transactions.slice(0, INITIAL_DISPLAY_COUNT);

  const hasMore = transactions.length > INITIAL_DISPLAY_COUNT;

  useEffect(() => {
    const controller = new AbortController();

    const fetchTransactions = async () => {
      setAreTransactionsLoading(true);
      setError(null);

      const response = await getTransactions(controller.signal);

      if (response.error === "AbortError") return;

      if (response.success && response.data) {
        setTransactions(response.data);
      } else {
        setError(response.error);
      }
      setAreTransactionsLoading(false);
    };

    fetchTransactions();
    return () => controller.abort();
  }, []);

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        displayedTransactions,
        areTransactionsLoading,
        error,
        isExpanded,
        toggleExpanded,
        hasMore
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransactionContext = () => useContext(TransactionContext);
